// src/components/Widgets/MarketsChecklistWidget.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateTo, ROUTES } from "../../lib/nav";
import { useVault } from "../../contexts/VaultContext";
import WidgetHeader from "../Common/WidgetHeader";

/**
 * MarketsChecklistWidget
 * - Vault-backed: reads/writes "markets/checklists.json" + "markets/templates.json"
 * - Shows a small, quick list (default = the first checklist)
 * - Apply Template button to seed items from a template
 * - Link to full page /markets-checklists for deep editing & template shop
 */

const FILE_CHECKLISTS = "markets/checklists.json";
const FILE_TEMPLATES  = "markets/templates.json";

const FREE_TEMPLATES = [
  {
    id: "tpl_basic_market",
    name: "Basic Market Day",
    items: [
      "Float / change",
      "Square/Stripe reader charged",
      "Table cloths",
      "Price tags / signage",
      "Packaging: paper bags, tissue, bubble wrap",
      "Business cards",
      "Stock list & inventory sheet",
      "Hand sanitiser & wipes",
      "Water & snacks",
      "Shade/gazebo + weights",
      "Extension lead & power board (if needed)",
    ],
  },
  {
    id: "tpl_ceramics_booth",
    name: "Ceramics Booth",
    items: [
      "Display risers / crates",
      "Props: flowers / greenery",
      "Glaze care card holder",
      "QR code sign for Instagram",
      "Spare price stickers & marker",
      "Small broom & cloth",
      "Emergency kit (tape, scissors, zip ties)",
      "Spare light bulbs (if night)",
    ],
  },
];

function useVaultJson(key, fallback) {
  const { ready, getJSON, setJSON } = useVault();
  const [value, setValue] = useState(fallback);
  
  // Load from vault
  useEffect(() => {
    let active = true;
    if (!ready) return;
    (async () => {
      const v = await getJSON(key, fallback);
      if (active) setValue(v ?? fallback);
    })();
    return () => { active = false; };
  }, [ready, key, getJSON]);

  // Save to vault with debounce to reduce calls and ensure Drive persistence
  useEffect(() => {
    if (!ready || !value) return;
    const timer = setTimeout(() => {
      setJSON(key, value);
    }, 500);
    return () => clearTimeout(timer);
  }, [ready, key, value, setJSON]);
  
  return [value, setValue];
}

export default function MarketsChecklistWidget({ isDark }) {
  const navigate = useNavigate();
  const { setJSON } = useVault();
  const [data, setData] = useVaultJson(FILE_CHECKLISTS, { lists: [], updatedAt: null });
  const [templates, setTemplates] = useVaultJson(FILE_TEMPLATES, { free: FREE_TEMPLATES, pro: [] });
  const [newItem, setNewItem] = useState("");
  const [activeListId, setActiveListId] = useState(null);

  // Ensure one default list exists
  useEffect(() => {
    if (!Array.isArray(data?.lists) || data.lists.length === 0) {
      const seed = {
        id: `list_${Date.now()}`,
        title: "Market Day",
        items: [],
      };
      setData({ lists: [seed], updatedAt: new Date().toISOString() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const list = useMemo(() => {
    if (!data?.lists?.length) return null;
    const id = activeListId || data.lists[0]?.id;
    return data.lists.find((l) => l.id === id) || data.lists[0];
  }, [data, activeListId]);
  const items = list?.items || [];

  const saveList = async (nextItems) => {
    const updatedLists = (data?.lists || []).map((l) => 
      l.id === list?.id ? { ...l, items: nextItems } : l
    );
    const next = { lists: updatedLists, updatedAt: new Date().toISOString() };
    setData(next);
    // Force immediate save to vault (will sync to Drive and local folder)
    try {
      await setJSON(FILE_CHECKLISTS, next);
    } catch (err) {
      console.error("Failed to save checklist to Drive/folder:", err);
    }
    try { 
      window.dispatchEvent(new Event("mudo:markets-checklists-updated")); 
    } catch {}
  };

  const toggle = (id) => {
    const next = items.map((it) => it.id === id ? { ...it, done: !it.done } : it);
    saveList(next);
  };
  const add = () => {
    const txt = (newItem || "").trim();
    if (!txt) return;
    const entry = { id: `it_${Date.now().toString(36)}`, text: txt, done: false };
    saveList([entry, ...items]);
    setNewItem("");
  };
  const remove = (id) => {
    saveList(items.filter((it) => it.id !== id));
  };
  const clearDone = () => {
    saveList(items.filter((it) => !it.done));
  };
  const applyTemplate = (tpl) => {
    if (!tpl) return;
    const seeded = (tpl.items || []).map((t) => ({ id: `it_${Math.random().toString(36).slice(2, 8)}`, text: t, done: false }));
    saveList([...seeded, ...items]);
  };

  const ring = isDark ? "1px solid rgba(255,255,255,.12)" : "1px solid rgba(0,0,0,.08)";
  const cardBg = isDark ? "rgba(0,0,0,.25)" : "rgba(255,255,255,.9)";
  const textColor = isDark ? "#ffffff" : "#000000";
  const inputBg = isDark ? "rgba(255,255,255,.06)" : "rgba(255,255,255,.9)";

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <WidgetHeader title="Markets Checklist" className="mb-0" />
        <div className="flex items-center gap-2">
          <button
            onClick={() => clearDone()}
            className="px-2 py-1 rounded text-xs"
            style={{ border: ring, color: textColor }}
            title="Clear completed items"
          >
            Clear done
          </button>
          <button
            onClick={() => navigateTo(navigate, ROUTES.MARKETS_CHECKLISTS, { source: 'MarketsChecklistWidget:open' })}
            className="px-2 py-1 rounded text-xs"
            style={{ background: "var(--mudo-primary, #f59e0b)", color: "var(--mudo-primary-contrast, #111)" }}
            title="Open full editor"
          >
            Open
          </button>
        </div>
      </div>

      {/* List tabs */}
      {(data?.lists?.length || 0) > 1 && (
        <div className="flex gap-1 overflow-x-auto pb-1">
          {(data?.lists || []).map((l) => (
            <button
              key={l.id}
              onClick={() => setActiveListId(l.id)}
              className="px-3 py-1 rounded text-xs whitespace-nowrap transition-colors"
              style={{
                border: ring,
                background: list?.id === l.id ? "var(--mudo-primary, #f59e0b)" : inputBg,
                color: list?.id === l.id ? "#000" : textColor,
                fontWeight: list?.id === l.id ? "600" : "400"
              }}
            >
              {l.title}
            </button>
          ))}
        </div>
      )}

      {/* Quick add */}
      <div className="flex gap-2">
        <input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="Add a task…"
          className="flex-1 px-2 py-1 rounded"
          style={{ border: ring, background: inputBg, color: textColor }}
        />
        <button
          onClick={add}
          className="px-3 py-1 rounded text-sm"
          style={{ background: "var(--mudo-primary, #f59e0b)", color: "var(--mudo-primary-contrast, #111)" }}
        >
          Add
        </button>
      </div>

      {/* Templates row */}
      <div className="flex flex-wrap gap-2">
        {(templates?.free || []).slice(0, 3).map((tpl) => (
          <button
            key={tpl.id}
            onClick={() => applyTemplate(tpl)}
            className="px-2 py-1 rounded text-xs"
            style={{ border: ring, background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.04)", color: textColor }}
            title={`Apply template: ${tpl.name}`}
          >
            + {tpl.name}
          </button>
        ))}
        <button
          onClick={() => navigateTo(navigate, ROUTES.MARKETS_CHECKLISTS, { source: 'MarketsChecklistWidget:templates' })}
          className="px-2 py-1 rounded text-xs"
          style={{ border: ring, color: textColor }}
          title="Browse all templates"
        >
          Templates…
        </button>
      </div>

      {/* List */}
      <div className="rounded-lg p-2 space-y-1" style={{ border: ring, background: cardBg }}>
        {items.length === 0 ? (
          <div className="text-sm opacity-70" style={{ color: textColor }}>No items yet — add one or apply a template.</div>
        ) : (
          items.slice(0, 10).map((it) => (
            <label
              key={it.id}
              className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white/5"
              style={{ cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={!!it.done}
                onChange={() => toggle(it.id)}
              />
              <span className={`text-sm ${it.done ? "line-through opacity-60" : ""}`} style={{ color: textColor }}>{it.text}</span>
              <button
                onClick={(e) => { e.stopPropagation(); remove(it.id); }}
                className="ml-auto text-xs opacity-70 hover:opacity-100"
                title="Remove"
              >
                Remove
              </button>
            </label>
          ))
        )}
        {items.length > 10 && (
          <div className="text-xs opacity-70 mt-1">Showing first 10 — open to see all.</div>
        )}
      </div>
    </div>
  );
}
